import React, { useState, useMemo, useEffect } from 'react';
import { Clear } from '@mui/icons-material';

import { Input } from '../Input';
import { Dropdown } from '../Dropdown';

interface AutoCompleteProps {
  options: OptionType[];
  disabled?: boolean;
  value?: string;
  className?: string;
  placeholder?: string;
  restoreOnBlur?: boolean;
  onChange?: (newValue: string) => void;
}

export const AutoComplete: React.FC<AutoCompleteProps> = ({
  options,
  disabled,
  value,
  restoreOnBlur,
  onChange,
  ...rest
}) => {
  const getInitialLabel = () => {
    const matched = options.find(option => option.value === value);
    return matched?.label ?? '';
  };

  const [query, setQuery] = useState<string>(String(getInitialLabel()));
  const [isFocused, setIsFocused] = useState(false);
  const [shouldFocus, setShouldFocus] = useState(false);
  const blurTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const matched = options.find(option => option.value === value);
    if (matched) {
      setQuery(matched.label as string);
    } else if (!value) {
      setQuery('');
    }
  }, [value, options]);

  useEffect(() => {
    if (shouldFocus) {
      setShouldFocus(false);
    }
  }, [shouldFocus]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const filteredOptions = useMemo(() => {
    if (!query) return options;

    const lowerCaseQuery = query.toLocaleLowerCase('en');
    const collator = new Intl.Collator('en', { sensitivity: 'base' });

    return options.filter(option => {
      const label =
        typeof option.label === 'string'
          ? option.label
          : String(option.label ?? '');

      return (
        collator.compare(label.toLocaleLowerCase('en'), lowerCaseQuery) === 0 ||
        label.toLocaleLowerCase('en').includes(lowerCaseQuery)
      );
    });
  }, [query, options]);

  const hasValue = query.length > 0;

  return (
    <Dropdown
      {...rest}
      emptyMessage="No Options found"
      options={filteredOptions}
      value={value}
      disableMissingValueOption
      onChange={(newValue, option) => {
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        onChange?.(newValue);
        setQuery((option?.label as string) ?? '');
        setIsFocused(false);
      }}
      disabled={disabled}
      open={isFocused}
      withArrow={!hasValue}
      onToggle={open => setIsFocused(open)}
      renderValue={({ ...props }) => (
        <div onClick={e => e.stopPropagation()} className="w-full">
          <Input
            {...props}
            label=""
            withBorder
            value={query}
            className="w-full"
            onChange={setQuery}
            onFocus={() => {
              if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
                blurTimeoutRef.current = null;
              }
              setIsFocused(true);
            }}
            onBlur={() => {
              blurTimeoutRef.current = setTimeout(() => {
                setIsFocused(false);
                if (restoreOnBlur) {
                  setQuery(String(getInitialLabel()));
                }
              }, 200);
            }}
            autoFocus={shouldFocus}
            suffix={
              hasValue && (
                <div
                  className="absolute right-0 h-full top-0 inline-flex items-center justify-center"
                  onClick={e => {
                    e.stopPropagation();
                    setQuery('');
                    if (!restoreOnBlur) {
                      onChange?.('');
                    }
                    setShouldFocus(true);
                  }}
                >
                  <Clear className="hover:bg-buttonLight rounded-full" />
                </div>
              )
            }
            inputClassName="pr-2xl cursor-pointer"
          />
        </div>
      )}
    />
  );
};
